import {reactive, watch, type WatchStopHandle} from 'vue';
import {apps, BackendFetch, type Commands, type Models, useLiveQueries, useProcesses, type ViewContexts} from '@hantera/portal-app';
import cleanAssign from "@/clean-assign";
import {mergeQueryNode} from "@/backend/graph";



const baseQuery = {

  type: 'query',
  body: {
    query: {
      fields: [
        "orderId",
        "orderNumber",
        "channelKey",
        "currencyCode",
        "customerNumber",
        "invoiceRecipient",
        "locale",
        "notes",
        "orderState",
        "orderTaxTotal",
        "orderTotal",
        "taxIncluded",
        "tags",
        "dynamic",
        "createdAt",
        "cancelledAt"
      ],
      navigate: [{
        edge: 'orderDiscounts',
        node: {
          fields: [
            'discountId',
            'discountType',
            'createdAt',
            'calculatedTotal',
            'value',
            'description',
            'dynamic',
          ]
        }
      }, {
        edge: "deliveries",
        node: {
          fields: [
            "deliveryId",
            "deliveryNumber",
            "deliveryState",
            "deliveryAddress",
            "createdAt",
            "finalizedAt",
            "shippingPrice",
            "shippingTaxFactor",
            "shippingTax",
            "shippingTotal",
            "shippingProductNumber",
            "shippingDescription",
            "releasedToInvoice",
            "tags",
            "dynamic",
          ],
          navigate: [{
            edge: "orderLines",
            node: {
              fields: [
                "orderLineId",
                "orderLineNumber",
                "productNumber",
                "description",
                "image",
                "quantity",
                "returnedQuantity",
                "reservedQuantity",
                "unitPrice",
                "taxFactor",
                "taxTotal",
                "orderLineTotal",
                "releasedToInvoice",
                "dynamic",
              ],
              navigate: [{
                edge: "calculatedDiscounts",
                node: {
                  fields: [
                    "calculatedDiscountId",
                    "referenceId",
                    "referenceType",
                    "value"
                  ],
                }
              },{
                edge: 'orderLineDiscounts',
                node: {
                  fields: [
                    'discountId',
                    'discountType',
                    'createdAt',
                    'calculatedTotal',
                    'value',
                    'description',
                    'dynamic',
                  ]
                }
              },{
                edge: "backOrders",
                node: {
                  fields: [
                    "quantity",
                  ],
                }
              },{
                edge: "skus",
                node: {
                  fields: [
                    "skuNumber",
                    "totalQuantity",
                    "reservedQuantity",
                    "unitQuantity",
                  ],
                  navigate: [{
                    edge: "stockReservations",
                    node: {
                      fields: [
                        "stockReservationId",
                        "inventoryDate",
                        "quantity",
                      ],
                      navigate: [{
                        edge: 'stockPosition',
                        node: {
                          fields: ['inventoryKey']
                        }
                      },{
                        edge: 'allocation',
                        node: {
                          fields: ['reference']
                        }
                      }]
                    }
                  },{
                    edge: "backOrders",
                    node: {
                      fields: [
                        "backOrderId",
                        "inventoryDate",
                        "quantity",
                      ],
                      navigate: [{
                        edge: 'stockPosition',
                        node: {
                          fields: ['inventoryKey']
                        }
                      }]
                    }
                  }]
                }
              }]
            }
          },{
            edge: "calculatedShippingDiscounts",
            node: {
              fields: [
                "calculatedDiscountId",
                "referenceId",
                "referenceType",
                "value"
              ],
            }
          },{
            edge: 'shippingDiscounts',
            node: {
              fields: [
                'discountId',
                'discountType',
                'createdAt',
                'calculatedTotal',
                'value',
                'description',
                'dynamic',
              ]
            }
          }]
        }
      }, {
        edge: "invoices",
        node: {
          fields: [
            "invoiceId",
            "invoiceNumber",
            "invoiceTotal",
            "createdAt",
            "invoiceRecipient",
            "invoiceNetTotal",
            "invoiceTaxTotal",
            "isCancelled"
          ],
          navigate: [{
            edge: "invoiceLines",
            node: {
              fields: [
                "quantity",
                "net",
                "tax",
                "total",
                "description",
                "invoiceLineId",
                "invoiceLineNumber",
                "productNumber",
                "referenceType"
              ],
              navigate: [{
                edge: "discounts",
                node: {
                  fields: [
                    "amount",
                    "referenceId",
                    "referenceType",
                    "invoiceLineDiscountId",
                    "description"
                  ]
                }
              }]
            },
          },{
            edge: "paymentCaptures",
            node: {
              fields: [
                "amount",
                "capturedAmount",
                "createdAt",
                "paymentCaptureId",
                "remainingAmount",
                "state"
              ],
              navigate: [{
                edge: "payment",
                node: {
                  fields: ["paymentId", "paymentNumber"]
                }
              },{
                edge: "invoice",
                node: {
                  fields: ["invoiceNumber"]
                }
              }]
            }
          },{
            edge: "orderJournalEntries",
            node: {
              fields: [
                "orderJournalEntryId",
                "amount",
                "orderJournalEntryType",
                "timestamp"
              ],
              navigate: [{
                edge: "invoice",
                node: {
                  fields: [
                    "invoiceId",
                    "invoiceNumber"
                  ]
                }
              }
              ]
            }
          }]
        }
      }, {
        edge: "journalEntries",
        node: {
          fields: [
            "amount",
            "orderJournalEntryId",
            "orderJournalEntryType",
            "timestamp",
          ]
        }
      }, {
        edge: "payments",
        node: {
          fields: [
            "paymentId",
            "paymentNumber",
            "externalReference",
            "channelKey",
            "authorizedAmount",
            "providerKey",
            "tags",
            "capturedBalance",
            "creditBalance",
            "chargedBalance",

            "dynamic",
            "currencyCode",
          ],
          navigate: [{
            edge: "journalEntries",
            node: {
              fields: [
                "amount",
                "captureId",
                "invoiceId",
                "orderId",
                "paymentJournalEntryId",
                "paymentJournalEntryType",
                "timestamp",
                "transactionReference",
                "paymentJournalEntryNumber",
              ],
              navigate: [{
                edge: "invoice",
                node: {
                  fields: ["invoiceNumber"]
                }
              }]
            }
          },{
            edge: "captures",
            node: {
              fields: [
                "amount",
                "capturedAmount",
                "createdAt",
                "paymentCaptureId",
                "remainingAmount",
                "state"
              ],
              navigate: [{
                edge: "payment",
                node: {
                  fields: ["paymentId", "paymentNumber"]
                }
              },{
                edge: "invoice",
                node: {
                  fields: ["invoiceNumber"]
                }
              }]
            }
          }]
        }
      }, {
        edge: "activityLogs",
        node: {
          fields: [
            "activityLogId",
            "messageTemplate",
            "type",
            "dynamic",
            "createdAt",
          ],
          navigate: [{
            edge: 'identity',
            node: {
              fields: ['identityId', 'name', 'type']
            }
          }]
        }
      },{
        edge: "checkpoints",
        node: {
          fields: [
            "checkpointId",
            "timestamp"
          ],
          navigate: [{
            edge: 'identity',
            node: {
              fields: ['identityId', 'name', 'type']
            }
          }]
        }
      },{
        edge: "promotions",
        node: {
          fields: [
            "promotionId",
            "description",
            "promotionGroup",
            "isActive",
            "calculatedTotal",
            "messageType",
            "messageRendered",
            "dynamic",
            "createdAt",
          ]
        }
      },{
        edge: "customer",
        node: {
          fields: ["dynamic", "createdAt"],
          navigate: [{
            edge: "orders",
            node: {
              fields: ["orderId", "orderNumber", "orderState", "orderTotal", "currencyCode", "tags", "createdAt"]
            }
          }]
        }
      }]
    }
  }
};

export class OrderViewContextImpl implements ViewContexts.OrderViewContext {
  
  readonly state: Models.Graph.Order = reactive({} as any);
  private readonly redoStash: ViewContexts.PendingChange<Commands.Order.OrderCommand>[] = []; 
  private readonly history: ViewContexts.PendingChange<Commands.Order.OrderCommand>[] = [];
  private readonly internalHistoryState = reactive({
    hasPending: false,
    canRedo: false
  });
  private readonly internalWorkState = reactive({
    isSaving: false,
    isRefreshing: false
  });
  private readonly internalTimetravelState = reactive({
    isActive: false,
    checkpointId: null as string | null,
    timestamp: null as Date | null
  });
  private refreshController:AbortController | undefined;

  private liveQuery: ReturnType<ReturnType<typeof useLiveQueries>['query']> | undefined;
  private liveQueryWatchStop: WatchStopHandle | undefined;
  private liveRefreshDebounce: ReturnType<typeof setTimeout> | undefined;

  private readonly query: typeof baseQuery;

  constructor (
    private readonly orderSelector: {
      type: 'orderId' | 'orderNumber'
      value: string
    },
    private readonly fetch: BackendFetch,
    private readonly procs: ReturnType<typeof useProcesses>,
    private readonly liveQueries?: ReturnType<typeof useLiveQueries>,
    queryExtensions?: apps.standardServices.OrderQueryNodeExtension[]
  ) {
    if (orderSelector.type === 'orderId') {
      this.state.orderId = orderSelector.value;
    } else {
      this.state.orderNumber = orderSelector.value;
    }

    let queryNode = baseQuery.body.query;
    for (const extension of queryExtensions ?? []) {
      queryNode = mergeQueryNode(queryNode, extension);
    }
    this.query = {
      ...baseQuery,
      body: {
        ...baseQuery.body,
        query: queryNode
      }
    };
  }


  startLiveUpdates() {
    if (!this.liveQueries?.available || this.liveQuery) {
      return;
    }

    const orderId = this.state.orderId;
    const orderNumber = this.state.orderNumber;
    const filter = orderId
      ? `orderId == ${orderId}`
      : `orderNumber == '${(orderNumber ?? '').replaceAll("'", "''")}'`;

    const spec = {
      edge: 'orders',
      filter,
      node: this.query.body.query
    };


    this.liveQuery = this.liveQueries.query<Models.Graph.Order>(spec);
    this.liveQueryWatchStop = watch(
      () => this.liveQuery!.revision.value,
      () => this.onLiveSignal()
    );
  }

  private onLiveSignal() {
    const node = this.liveQuery?.nodes.value[0];

    const usePreview = this.internalHistoryState.hasPending
      || this.internalTimetravelState.isActive;

    if (usePreview) {
      if (this.liveRefreshDebounce) {
        clearTimeout(this.liveRefreshDebounce);
      }
      this.liveRefreshDebounce = setTimeout(() => {
        this.liveRefreshDebounce = undefined;
        void this.refresh();
      }, 50);
      return;
    }

    if (node && !this.internalWorkState.isSaving) {
      cleanAssign(this.state, node);
    }
  }

  dispose() {
    if (this.liveRefreshDebounce) {
      clearTimeout(this.liveRefreshDebounce);
      this.liveRefreshDebounce = undefined;
    }
    this.liveQueryWatchStop?.();
    this.liveQueryWatchStop = undefined;
    this.liveQuery?.dispose();
    this.liveQuery = undefined;
  }

  get historyState() {
    return this.internalHistoryState;
  }

  get workState() {
    return this.internalWorkState;
  }

  get timetravelState() {
    return this.internalTimetravelState;
  }
  
  private refreshHistoryState() {
    this.internalHistoryState.hasPending = this.history.length > 0;
    this.internalHistoryState.canRedo = this.redoStash.length > 0;
  }

  undo(fromError = false) {
    if (this.internalWorkState.isSaving) {
      return false;
    }
    
    if (this.history.length === 0) {
      return false;
    }
    
    const cut = this.history.pop();
    if (!fromError) {
      this.redoStash.push(cut!);
      this.refreshHistoryState();
    }
    return true;
  }
  redo() {
    if (this.internalWorkState.isSaving) {
      return false;
    }
    
    if (this.redoStash.length > 0) {
      const cut = this.redoStash.pop();
      this.history.push(cut!);
      this.refreshHistoryState();
      return true;
    }
    return false;
  }
  
  private getCommandsToApply() {
    // Get the latest changeSet per changeId by going through history in reverse and add the first unique changeId
    const commands: Commands.Order.OrderCommand[] = [];
    const set = new Set<string>();
    for (let i = this.history.length - 1; i >= 0; i--) {
      const entry = this.history[i];
      if (entry.changeId) {
        if (set.has(entry.changeId!)) {
          continue;
        }
        set.add(entry.changeId!);
      }

      commands.splice(
        0,
        0,
        ...([...entry.commands, {
          type: 'addActivityLog',
          messageTemplate: entry.messageTemplate,
          dynamic: entry.messageDynamic
        }] as Commands.Order.OrderCommand[])
      );
    }
    return commands;
  }

  async save(): Promise<Commands.Order.OrderCommand[]> {
    
    if (this.internalWorkState.isSaving) {
      console.error('A save of an order was attempted but order was already being saved.');
      return [];
    }
    
    const applyCommands = this.getCommandsToApply();
    if (applyCommands.length === 0) {
      return [];
    }

    
    if (this.refreshController && this.internalWorkState.isRefreshing) {
      this.refreshController.abort('stale');
    }
    
    try {
      // Block refresh/undo/redo actions
      this.internalWorkState.isSaving = true;
      this.historyState.hasPending = false;
      this.historyState.canRedo = false;

      const body = [{
        type: 'applyCommands',
        body: {
          commands: applyCommands
        }
      }, this.query];


      const response = await this.fetch(`/core/resources/actors/order/${this.orderSelector.value}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error('Failed to execute query.', {
          cause: await response.json()
        });
      }

      const result = await response.graph()

      cleanAssign(this.state, result.data.query);

      this.history.splice(0, this.history.length);
      this.redoStash.splice(0, this.redoStash.length);

      return applyCommands;
    }
    catch (e) {
      // TODO Display error
      return [];
    }
    finally
    {
      this.internalWorkState.isSaving = false;
      this.refreshHistoryState();
    }
  }

  
  async refresh() {
    if (this.internalWorkState.isSaving) {
      return;
    }
    
    try {

      this.internalWorkState.isRefreshing = true;
      if (this.refreshController && this.internalWorkState.isRefreshing) {
        this.refreshController.abort('stale');
      }
      this.refreshController = new AbortController();

      let isPreview = false;
      const body = [];

      // Apply timetravel rewind if active
      if (this.internalTimetravelState.isActive && this.internalTimetravelState.checkpointId) {
        isPreview = true;
        body.push({
          type: 'rewind',
          body: {
            checkpointId: this.internalTimetravelState.checkpointId
          }
        });
      }

      // Apply pending command preview
      const previewCommands = this.getCommandsToApply();
      if (previewCommands.length > 0) {
        isPreview = true;
        body.push({
          type: 'applyCommands',
          body: {
            commands: previewCommands
          }
        });
      }

      body.push(this.query);

      const response = await this.fetch(
        `/core/resources/actors/order/${this.orderSelector.value}?validation=warn${(isPreview ? '&preview' : '')}`, {

          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body),
          signal: this.refreshController.signal
        });

      if (!response.ok) {
        throw new Error('Failed to execute query.', {
          cause: await response.errors()
        });
      }

      const result = await response.graph()

      cleanAssign(this.state, result.data.query);
    }
    catch (err: any)
    {
      if (err !== 'stale') {
        throw err;
      }
    }
    finally
    {
      this.internalWorkState.isRefreshing = false;
    }
  }

  async pushPendingChange(
    changeId: string | undefined,
    commands: Commands.Order.OrderCommand[],
    messageTemplate: string,
    messageDynamic?: Record<string, any>,
  ) {
    if (this.internalTimetravelState.isActive) {
      this.procs.error({
        message: 'Cannot push pending changes while in timetravel mode. Exit timetravel mode first.' 
      })
      return false
    }

    this.history.push({
      changeId,
      commands,
      messageTemplate: messageTemplate,
      messageDynamic: messageDynamic ?? {}
    });
    if (this.redoStash.length > 0) {
      this.redoStash.splice(0, this.redoStash.length);
    }
    
    const internalRefresh: () => Promise<boolean> = async () =>  {
      try
      {
        await this.refresh()
        this.refreshHistoryState();
        
        return true
      }
      catch (err: any) {
        if (err instanceof Error) {
          if (Array.isArray(err.cause)) {

            for (const error of err.cause) {
              this.procs.error(error)
            }
            
            if (this.undo(true)) {
              return await internalRefresh()
            }
          }
        }
        return false
      }
    }

    return await internalRefresh()
  }

  async enterTimetravel(checkpointId: string, timestamp: Date) {
    if (this.internalHistoryState.hasPending) {
      throw new Error('Cannot enter timetravel mode with pending changes. Please save or discard changes first.');
    }

    this.internalTimetravelState.isActive = true;
    this.internalTimetravelState.checkpointId = checkpointId;
    this.internalTimetravelState.timestamp = timestamp;

    await this.refresh();
  }

  async exitTimetravel() {
    this.internalTimetravelState.isActive = false;
    this.internalTimetravelState.checkpointId = null;
    this.internalTimetravelState.timestamp = null;

    await this.refresh();
  }

  async commitTimetravel() {
    if (!this.internalTimetravelState.isActive || !this.internalTimetravelState.checkpointId) {
      return;
    }

    if (this.internalWorkState.isSaving || this.internalWorkState.isRefreshing) {
      return;
    }

    try {
      this.internalWorkState.isSaving = true;

      const body = [{
        type: 'rewind',
        body: {
          checkpointId: this.internalTimetravelState.checkpointId
        }
      }, this.query];


      const response = await this.fetch(
        `/core/resources/actors/order/${this.orderSelector.value}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        });

      if (!response.ok) {
        throw new Error('Failed to commit rewind.', {
          cause: await response.errors()
        });
      }

      const result = await response.graph();

      cleanAssign(this.state, result.data.query);

      this.internalTimetravelState.isActive = false;
      this.internalTimetravelState.checkpointId = null;
      this.internalTimetravelState.timestamp = null;
    }
    catch (e) {
      // TODO Display error
      console.error('Failed to commit timetravel:', e);
      throw e;
    }
    finally {
      this.internalWorkState.isSaving = false;
    }
  }
  
}
